import { Directive, HostBinding, Input, HostListener } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Location } from '@angular/common';
import { filter } from 'rxjs/operators';

@Directive({
    selector: '[appBack]',
    standalone: false
})
export class HistoryBackDirective {
  @Input() fallbackUrl = '/dashboard';

  @Input() disallowedPrefixes: string[] = ['/login'];
  @Input() stripLastNumericSegmentOnDisallowed = true;

  private static routerHistory: string[] = []; // router Urls
  private static wired = false; // check if router event was triggered
  private static historyIndex = -1; // current position in routerHistory


  // hideBackButton flag for OTP users (iframe)
  @HostBinding('style.display')
  get backButtonDisplay(): string | null {
    if (this.shouldHideBackButton()) {
      return 'none';
    }
    return null;
  }

  private shouldHideBackButton(): boolean {
    const navigationState = this.location.getState() as { hideBackButton?: boolean; } | null;
    return navigationState?.hideBackButton === true;
  }

  constructor(private router: Router, private location: Location) {
    if (!HistoryBackDirective.wired) {
      HistoryBackDirective.wired = true;

      this.router.events
        .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
        .subscribe((e) => {
          const url = e.urlAfterRedirects;
          const history = HistoryBackDirective.routerHistory;

          // reset app history
          if (this.isAuthenticationUrl(url)) {
            history.length = 0;
            HistoryBackDirective.historyIndex = -1;
            return;
          }

          // successful "transparent OTP" login starts a new history
          if (this.shouldHideBackButton()) {
            history.length = 0;
            history.push(url);
            HistoryBackDirective.historyIndex = 0;
            return;
          }

          // bugfix: avoid duplicates like "A->B->A->B" loops
          if (HistoryBackDirective.historyIndex === -1) {
            history.push(url);
            HistoryBackDirective.historyIndex = 0;
            return;
          }

          const idx = HistoryBackDirective.historyIndex;

          // prevent adding same URL
          if (history[idx] === url) return;

          // if back navigation (previous entry matches), move index back
          if (idx > 0 && history[idx - 1] === url) {
            HistoryBackDirective.historyIndex = idx - 1;
            return;
          }

          // if forward navigation (next entry matches), move index forward
          if (idx < history.length - 1 && history[idx + 1] === url) {
            HistoryBackDirective.historyIndex = idx + 1;
            return;
          }

          // otherwise push new navigation
          history.splice(idx + 1);
          history.push(url);
          HistoryBackDirective.historyIndex = history.length - 1;

          const historyStorageLimit = 50;
          if (history.length > historyStorageLimit) {
            const overflowItemsToTrim = history.length - historyStorageLimit;
            history.splice(0, overflowItemsToTrim);
            HistoryBackDirective.historyIndex = Math.max(0, HistoryBackDirective.historyIndex - overflowItemsToTrim);
          }
        });
    }
  }

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent) {
    event.preventDefault();

    if (this.shouldHideBackButton()) {
      return;
    }

    const current = this.router.url;

    const target = this.getPreviousAllowedUrl(current);

    if (target == null) {
      this.router.navigateByUrl(this.fallbackUrl);
      return;
    }

    const prevUrl = target.url;

    if (prevUrl && this.isDisallowed(prevUrl)) {
      const safe = this.stripLastNumericSegmentOnDisallowed
        ? this.stripToCollectionUrl(current)
        : this.fallbackUrl;

      this.router.navigateByUrl(safe || this.fallbackUrl, { replaceUrl: true });
      return;
    }

    // normal behaviour: go back in REAL browser history
    const steps = target.steps; // negative number
    this.location.historyGo(steps);
  }

  private getPreviousAllowedUrl(current: string): { url: string; steps: number } | null {
    const history = HistoryBackDirective.routerHistory;
    const idx = HistoryBackDirective.historyIndex;

    // iterate from previous history item (relative to current index)
    for (let i = idx - 1; i >= 0; i--) {
      const url = history[i];
      if (!url) continue;

      // skip OTP screens as Back destination
      if (this.isAuthenticationUrl(url)) continue;

      // skip duplicates of current
      if (this.normalizePath(url) === this.normalizePath(current)) continue;

      // skip the "edit of current" to prevent loops:
      // current: /users/<id>  prev: /users/<id>/edit
      if (this.isEditOfCurrent(url, current)) continue;

      return { url, steps: i - idx };
    }

    return null;
  }

  private normalizePath(url: string): string {
    return url.split(/[?#]/)[0].replace(/\/+$/, '');
  }

  private isEditOfCurrent(prevUrl: string, currentUrl: string): boolean {
    const prev = this.normalizePath(prevUrl);
    const current = this.normalizePath(currentUrl);
    return prev === `${current}/edit`;
  }

  private isAuthenticationUrl(url: string): boolean {
    const path = this.normalizePath(url);

    // Back btn routes black list
    const authenticationPrefixes = [
      '/login',
      '/embedded',
      '/otp',
      '/reset-password',
      '/new-password'
    ];

    // root route
    if (path === '') {
      return true;
    }

    return authenticationPrefixes.some(prefix => {
      return path === prefix || path.startsWith(`${prefix}/`);
    });
  }

  private isDisallowed(url: string): boolean {
    // block auth URLs from being back-navegables
    if (this.isAuthenticationUrl(url)) {
      return true;
    }

    return this.disallowedPrefixes.some((p) => url.startsWith(p));
  }

  /**
   *  SAMPLE
   * "/retailers/7" -> "/retailers"
   * "/retailers/7/manage-users" -> "/retailers"
   */
  private stripToCollectionUrl(url: string): string | null {
    const pathOnly = url.split(/[?#]/)[0];
    const segments = pathOnly.split('/').filter(Boolean);

    if (segments.length === 0) return null;

    // find last numeric segment (id)
    let lastIdIndex = -1;
    for (let i = segments.length - 1; i >= 0; i--) {
      if (/^\d+$/.test(segments[i])) {
        lastIdIndex = i;
        break;
      }
    }

    // if we find an id, return the path up the ID
    if (lastIdIndex > 0) {
      return '/' + segments.slice(0, lastIdIndex).join('/');
    }

    // if no ID go one path level up, removing last segment
    if (segments.length > 1) {
      return '/' + segments.slice(0, -1).join('/');
    }

    return null;
  }
}
